/**
 * User Store
 *
 * Manages user authentication and profile state with encrypted persistence.
 * Integrates with tokenService and sessionManager for auth lifecycle.
 *
 * Security: User data (including tokens, roles, and personal info) is encrypted
 * at rest using AES-256 encryption (OWASP A02 mitigation).
 *
 * @module store/user/userStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import logger from '../../shared/services/logging/logger';
import { tokenService } from '../../shared/services/auth/tokenService';
import { sessionManager } from '../../shared/services/sessionManagement/sessionManager';
import { secureStorage } from '../../shared/utils/secureStorage';
import { createPersistConfig } from '../middleware/persistenceMiddleware';
import { resetAllStores } from '../resetAllStores';

// Default user state
const DEFAULT_USER_DATA = {
  userId: '',
  name: 'User',
  username: '',
  surname: '',
  email: '',
  role: '',
  roles: [],
  accessToken: '',
  idToken: '',
  organizations: [],
  currentOrganization: null,
  currentOrganisationName: 'Your Organization',
  organizationId: '',
  divisionId: '',
  groupId: '',
  conversationsBucket: '',
  fileManagementBucket: '',
  customBuckets: false,
  projectsBucketName: '',
  projectsBucketRoleArn: ''
};

/**
 * Helper: Set user tokens in tokenService
 * @param {Object} tokens - Token data
 */
const setUserTokens = (tokens) => {
  const { idToken, accessToken, refreshToken, email } = tokens;

  if (accessToken || idToken) {
    tokenService.setTokens(accessToken || null, idToken || null, refreshToken || null);
  }

  if (email) {
    tokenService.setEmail(email);
  }
};

/**
 * Helper: Process roles from source data
 * @param {Object} source - Source data with roles
 * @param {string} fallbackRole - Fallback role
 * @returns {string} Primary role
 */
const processRole = (source, fallbackRole) => {
  if (source.roles) {
    if (Array.isArray(source.roles) && source.roles.length > 0) {
      return source.roles[0].replace('ROLE_', '');
    }
    if (typeof source.roles === 'string') {
      return source.roles.replace('ROLE_', '');
    }
  }
  if (source.role) {
    return source.role.replace('ROLE_', '');
  }
  return fallbackRole;
};

/**
 * Helper: Resolve organization-related fields
 * @param {Object} newData - New user data
 * @param {Object} prev - Previous user data
 * @returns {Object} Organization fields
 */
const resolveOrgFields = (newData, prev) => {
  const organizations = Array.isArray(newData.organizations)
    ? newData.organizations
    : (prev.organizations || []);

  let currentOrg = null;
  if (newData.currentOrganization) {
    currentOrg = newData.currentOrganization;
  } else if (newData.organizationId) {
    currentOrg = organizations.find(org =>
      org.id === newData.organizationId ||
      org.organizationId === newData.organizationId
    );
  }

  if (!currentOrg) {
    currentOrg = prev.currentOrganization || organizations[0] || null;
  }

  const organizationId = newData.organizationId ||
    currentOrg?.organizationId ||
    currentOrg?.id ||
    prev.organizationId ||
    '';

  const conversationsBucket = newData.conversationsBucket ||
    currentOrg?.conversationsBucket ||
    prev.conversationsBucket ||
    '';

  const fileManagementBucket = newData.fileManagementBucket ||
    currentOrg?.fileManagementBucket ||
    prev.fileManagementBucket ||
    '';

  const customBuckets = newData.customBuckets ||
    currentOrg?.customBuckets ||
    prev.customBuckets ||
    false;

  const projectsBucketName = newData.projectsBucketName ||
    currentOrg?.projectsBucketName ||
    prev.projectsBucketName ||
    '';

  const projectsBucketRoleArn = newData.projectsBucketRoleArn ||
    currentOrg?.projectsBucketRoleArn ||
    prev.projectsBucketRoleArn ||
    '';

  const currentOrganisationName = newData.currentOrganisationName ||
    currentOrg?.name ||
    organizations[0]?.name ||
    prev.currentOrganisationName;

  return {
    organizations,
    currentOrganization: currentOrg,
    currentOrganisationName,
    organizationId,
    conversationsBucket,
    fileManagementBucket,
    customBuckets,
    projectsBucketName,
    projectsBucketRoleArn
  };
};

/**
 * Helper: Initialize user data from storage
 * @param {Object} parsed - Parsed user data
 * @returns {Object} Initialized user data
 */
const initializeUserData = (parsed) => {
  const role = processRole(parsed, DEFAULT_USER_DATA.role);
  const orgFields = resolveOrgFields(parsed, DEFAULT_USER_DATA);

  return {
    userId: parsed.userId || '',
    name: parsed.name || DEFAULT_USER_DATA.name,
    username: parsed.username || DEFAULT_USER_DATA.username,
    surname: parsed.surname || DEFAULT_USER_DATA.surname,
    email: parsed.email || DEFAULT_USER_DATA.email,
    roles: Array.isArray(parsed.roles) ? parsed.roles : [],
    role,
    accessToken: parsed.accessToken || DEFAULT_USER_DATA.accessToken,
    idToken: parsed.idToken || DEFAULT_USER_DATA.idToken,
    divisionId: parsed.divisionId || '',
    groupId: parsed.groupId || '',
    ...orgFields
  };
};

/**
 * Helper: Merge new data into previous user state
 * @param {Object} prev - Previous user data
 * @param {Object} newData - New user data
 * @returns {Object} Merged user data
 */
const mergeUserData = (prev, newData) => {
  const role = processRole(newData, prev.role);
  const orgFields = resolveOrgFields(newData, prev);

  return {
    ...prev,
    ...newData,
    userId: newData.userId || prev.userId,
    role,
    roles: Array.isArray(newData.roles) ? newData.roles : prev.roles,
    accessToken: newData.accessToken || prev.accessToken,
    idToken: newData.idToken || prev.idToken,
    divisionId: newData.divisionId || prev.divisionId || '',
    groupId: newData.groupId || prev.groupId || '',
    ...orgFields
  };
};

/**
 * User store state and actions
 *
 * @typedef {Object} UserState
 * @property {Object} userData - User data object
 * @property {boolean} isHydrating - Whether store is hydrating from storage
 * @property {Function} updateUserData - Update user data (login/logout)
 * @property {Function} setHydrating - Set hydration status
 */

export const useUserStore = create(
  persist(
    (set, get) => ({
      // State
      userData: DEFAULT_USER_DATA,
      isHydrating: true,

      // Actions

      /**
       * Update user data (handles login and logout)
       * @param {Object|null} newData - New user data or null to logout
       * @param {Object} [options] - Options
       * @param {boolean} [options.isLogin] - When true, merges newData against
       *   DEFAULT_USER_DATA instead of the previous state. Prevents fields from
       *   a prior user's session (e.g. divisionId, groupId, bucket names) from
       *   leaking into a new user's session when the new login payload omits them.
       */
      updateUserData: (newData, options = {}) => {
        if (!newData) {
          // Logout - clear everything
          logger.info('User logging out - clearing session');
          sessionManager.clearSession();
          tokenService.clearAll();
          set({ userData: DEFAULT_USER_DATA });
          return;
        }

        logger.info('Updating user data', { userId: newData.userId, email: newData.email });

        // Start session when user data is set (login)
        sessionManager.startSession();

        // Update tokens
        setUserTokens(newData);

        // Merge with existing data (persistence handled by middleware).
        // On login, merge against a clean default instead of previous state
        // so a new user's session never inherits a prior user's leftover fields.
        set((state) => ({
          userData: mergeUserData(options.isLogin ? DEFAULT_USER_DATA : state.userData, newData)
        }));

        // Dispatch storage event for cross-tab sync
        window.dispatchEvent(new Event('storage'));
      },

      /**
       * Set hydrating status
       * @param {boolean} isHydrating - Hydration status
       */
      setHydrating: (isHydrating) => {
        set({ isHydrating });
      }
    }),
    createPersistConfig('userData', {
      partialize: (state) => ({ userData: state.userData }),
      encrypted: true // Enable encryption for sensitive user data
    })
  )
);

// Set up session expiry listener
if (typeof window !== 'undefined') {
  window.addEventListener('sessionExpired', () => {
    logger.warn('Session expired - clearing user data and all stores');
    useUserStore.setState({ userData: DEFAULT_USER_DATA });
    tokenService.clearAll();
    resetAllStores();
  });
}

// Deferred hydration on store initialization
if (typeof window !== 'undefined') {
  const hydrateUserStore = async () => {
    try {
      if (!sessionManager.isAuthenticated()) {
        useUserStore.getState().setHydrating(false);
        return;
      }

      // Prefer sessionStorage (tab-isolated) to prevent cross-tab session bleed.
      // Falls back to localStorage for fresh tabs with no session data yet.
      const parsed = (await secureStorage.getEncryptedSession('userData')) || (await secureStorage.getEncrypted('userData'));
      if (parsed) {
        // Handle Zustand persist format: { state: { userData: {...} }, version: N }
        const userData = parsed?.state?.userData || parsed;
        setUserTokens(userData);
        useUserStore.setState({
          userData: initializeUserData(userData),
          isHydrating: false
        });
        logger.debug('User store hydrated from encrypted storage');
      } else {
        useUserStore.getState().setHydrating(false);
      }
    } catch (error) {
      logger.error('Error hydrating user store', {
        error: error.message,
        stack: error.stack
      });
      sessionManager.clearSession();
      useUserStore.getState().setHydrating(false);
    }
  };

  // Defer parsing to an idle callback
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(hydrateUserStore, { timeout: 150 });
  } else {
    setTimeout(hydrateUserStore, 0);
  }
}

// Granular selector hooks

/**
 * Get full user data object
 * @returns {Object} User data
 */
export const useUserData = () =>
  useUserStore(state => state.userData);

/**
 * Get hydrating status
 * @returns {boolean} Hydration status
 */
export const useIsHydrating = () =>
  useUserStore(state => state.isHydrating);

/**
 * Get user ID
 * @returns {string} User ID
 */
export const useUserId = () =>
  useUserStore(state => state.userData.userId);

/**
 * Get user email
 * @returns {string} User email
 */
export const useUserEmail = () =>
  useUserStore(state => state.userData.email);

/**
 * Get user name
 * @returns {string} User name
 */
export const useUserName = () =>
  useUserStore(state => state.userData.name);

/**
 * Get user surname
 * @returns {string} User surname
 */
export const useUserSurname = () =>
  useUserStore(state => state.userData.surname);

/**
 * Get user roles
 * @returns {Array<string>} User roles
 */
export const useUserRoles = () =>
  useUserStore(state => state.userData.roles);

/**
 * Get primary user role
 * @returns {string} User role
 */
export const useUserRole = () =>
  useUserStore(state => state.userData.role);

/**
 * Get organization ID
 * @returns {string} Organization ID
 */
export const useOrganizationId = () =>
  useUserStore(state => state.userData.organizationId);

/**
 * Get division ID
 * @returns {string} Division ID
 */
export const useDivisionId = () =>
  useUserStore(state => state.userData.divisionId);

/**
 * Get group ID
 * @returns {string} Group ID
 */
export const useGroupId = () =>
  useUserStore(state => state.userData.groupId);

/**
 * Get conversations bucket
 * @returns {string} Conversations bucket name
 */
export const useConversationsBucket = () =>
  useUserStore(state => state.userData.conversationsBucket);

/**
 * Get file management bucket
 * @returns {string} File management bucket name
 */
export const useFileManagementBucket = () =>
  useUserStore(state => state.userData.fileManagementBucket);

/**
 * Get custom buckets enabled status
 * @returns {boolean} Custom buckets enabled
 */
export const useCustomBucketsEnabled = () =>
  useUserStore(state => state.userData.customBuckets);

/**
 * Get projects bucket name
 * @returns {string} Projects bucket name
 */
export const useProjectsBucketName = () =>
  useUserStore(state => state.userData.projectsBucketName);

/**
 * Get projects bucket role ARN
 * @returns {string} Projects bucket role ARN
 */
export const useProjectsBucketRoleArn = () =>
  useUserStore(state => state.userData.projectsBucketRoleArn);

/**
 * Get organizations list
 * @returns {Array} Organizations
 */
export const useOrganizations = () =>
  useUserStore(state => state.userData.organizations);

/**
 * Get current organization
 * @returns {Object|null} Current organization
 */
export const useCurrentOrganization = () =>
  useUserStore(state => state.userData.currentOrganization);

/**
 * Get current organization name
 * @returns {string} Organization name
 */
export const useOrganizationName = () =>
  useUserStore(state => state.userData.currentOrganisationName);

/**
 * Get access token
 * @returns {string} Access token
 */
export const useAccessToken = () =>
  useUserStore(state => state.userData.accessToken);

/**
 * Get ID token
 * @returns {string} ID token
 */
export const useIdToken = () =>
  useUserStore(state => state.userData.idToken);

/**
 * Check if user is authenticated
 * @returns {boolean} Authentication status
 */
export const useIsAuthenticated = () =>
  useUserStore(state => {
    const { userData } = state;
    const hasIdToken = !!(userData.idToken && userData.email);
    const hasAccessToken = !!(userData.accessToken && userData.email);
    const hasValidSession = sessionManager.isAuthenticated();

    return (hasIdToken || hasAccessToken) && hasValidSession;
  });

/**
 * Get all user actions
 * @returns {Object} User actions
 */
export const useUserActions = () =>
  useUserStore(
    useShallow(state => ({
      updateUserData: state.updateUserData,
      setHydrating: state.setHydrating
    }))
  );
